import { useState } from 'react'
import { Alert, Badge, Button, Checkbox, Code, Group, Modal, Select, Stack, TextInput } from '@mantine/core'
import backend from '../../backend/backend'
import DataList from '../../components/shared/DataList'
import PageHeader from '../../components/shared/PageHeader'
import StatusBadge from '../../components/shared/StatusBadge'
import { useAction, useResource, useT } from '../../hooks'
import Message from '../../i18n/Message'
import { keys, loadAgents } from '../../store/dataActionCreators'
import { toKobo } from '../../utils/format'

const PERMS = ['customers.register', 'applications.submit_device', 'applications.submit_cash', 'payments.record', 'payments.generate_link']

export default function Agents() {
  const t = useT()
  const { data, reload } = useResource(keys.agents, loadAgents)
  const { busy, run } = useAction()
  const [modal, setModal] = useState(null) // 'new' | agent row
  const [form, setForm] = useState({})
  const [created, setCreated] = useState(null)
  const openNew = () => { setForm({ email: '', name: '', phone: '', role: 'agent', cap: '', permissions: ['customers.register', 'applications.submit_device', 'payments.record'] }); setModal('new') }
  const openEdit = (a) => { setForm({ permissions: a.permissions || [], cap: a.max_financed_kobo ? String(a.max_financed_kobo / 100) : '' }); setModal(a) }
  const set = (k) => (v) => setForm({ ...form, [k]: v })
  const perms = (
    <Checkbox.Group value={form.permissions} onChange={set('permissions')} label={t('field_permissions')}>
      <Stack gap={6} mt={6}>{PERMS.map((p) => <Checkbox key={p} value={p} label={t(`perm_${p.replace('.', '_')}`)} />)}</Stack>
    </Checkbox.Group>
  )
  return (
    <>
      <PageHeader title="agents_title" actions={<Button onClick={openNew}><Message id="agent_add" /></Button>} />
      <DataList items={(data?.items || []).map((a) => ({ ...a, id: a.id }))} empty="empty_list" onRowClick={(a) => (a.role === 'agent' ? openEdit(a) : null)}
        columns={[
          { key: 'full_name', header: 'col_name' },
          { key: 'email', header: 'col_email', mobileHidden: true },
          { key: 'role', header: 'col_role', render: (a) => <Message id={`role_${a.role}`} /> },
          { key: 'mfa', header: 'col_2fa', render: (a) => <Badge color={a.mfa_enrolled ? 'green' : 'gray'}>{t(a.mfa_enrolled ? 'mfa_on' : 'mfa_off')}</Badge> },
          { key: 'status', header: 'col_status', render: (a) => <StatusBadge status={a.status === 'active' ? 'active' : 'cancelled'} /> },
        ]} />

      <Modal opened={modal === 'new'} onClose={() => setModal(null)} title={<Message id="agent_add" />} centered>
        <Stack>
          <TextInput label={t('field_email')} type="email" value={form.email} onChange={(e) => set('email')(e.currentTarget.value)} />
          <TextInput label={t('field_name')} value={form.name} onChange={(e) => set('name')(e.currentTarget.value)} />
          <TextInput label={t('field_phone')} type="tel" value={form.phone} onChange={(e) => set('phone')(e.currentTarget.value)} />
          <Select label={t('col_role')} value={form.role} onChange={set('role')} allowDeselect={false} data={[{ value: 'agent', label: t('role_agent') }, { value: 'admin', label: t('role_admin') }]} />
          {form.role === 'agent' && perms}
          <TextInput label={t('field_cap')} inputMode="decimal" value={form.cap} onChange={(e) => set('cap')(e.currentTarget.value)} />
          <Button loading={busy} disabled={!form.email || !form.name} onClick={async () => {
            const body = { email: form.email, name: form.name, phone: form.phone || undefined, role: form.role, permissions: form.permissions }
            if (form.cap) body.maxFinancedKobo = toKobo(form.cap)
            const res = await run(() => backend.createAgent(body), { onDone: reload })
            if (res) { setCreated(res); setModal(null) }
          }}><Message id="agent_create" /></Button>
        </Stack>
      </Modal>

      <Modal opened={!!created} onClose={() => setCreated(null)} title={<Message id="agent_created" />} centered>
        <Stack>
          <Alert color="orange"><Message id="agent_password_once" /></Alert>
          <Code fz="md">{created?.email}</Code>
          <Code fz="md">{created?.temporaryPassword}</Code>
        </Stack>
      </Modal>

      <Modal opened={modal && modal !== 'new'} onClose={() => setModal(null)} title={modal?.full_name} centered>
        {modal && modal !== 'new' && (
          <Stack>
            {perms}
            <TextInput label={t('field_cap')} inputMode="decimal" value={form.cap} onChange={(e) => set('cap')(e.currentTarget.value)} />
            <Group>
              <Button loading={busy} onClick={async () => {
                await run(() => backend.updateAgent(modal.id, { permissions: form.permissions, max_financed_kobo: form.cap ? toKobo(form.cap) : null }), { success: 'agent_saved', onDone: reload })
                setModal(null)
              }}><Message id="save" /></Button>
              <Button variant="light" color={modal.status === 'active' ? 'red' : 'green'} loading={busy} onClick={async () => {
                await run(() => backend.updateAgent(modal.id, { status: modal.status === 'active' ? 'suspended' : 'active' }), { success: 'agent_saved', onDone: reload })
                setModal(null)
              }}><Message id={modal.status === 'active' ? 'agent_suspend' : 'agent_activate'} /></Button>
              <Button variant="default" loading={busy} onClick={() => run(() => backend.resetMfa(modal.id), { success: 'mfa_reset_done', onDone: reload })}><Message id="mfa_reset" /></Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  )
}
